'use client';

import { Menu as BaseMenu } from '@base-ui/react/menu';
import { useCallback, useMemo, useState } from 'react';

import { type Component, type ComponentProps } from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { MenuItemContext } from './internal/MenuItemContext';
import {
  menuItemBaseStyles,
  menuItemFocusStyles,
} from './internal/menuItemStyles';
import { MenuSelectionContext } from './internal/MenuSelectionContext';
import { useMenuItemInteraction } from './internal/useMenuItemInteraction';

const menuMultiSelectItemClasses = {
  root: 'zds-menu-multi-select-item-root',
} as const;

const menuMultiSelectItemStyles = tv({
  extend: menuItemBaseStyles,
  base: [menuMultiSelectItemClasses.root],
});

interface MenuMultiSelectItemBaseProps {
  /**
   * Whether the item is selected (controlled).
   */
  isSelected?: boolean;
  /**
   * Whether the item is selected by default (uncontrolled).
   *
   * @default false
   */
  defaultSelected?: boolean;
  /**
   * Handler that is called when the item's selected state changes.
   */
  onSelectedChange?: (isSelected: boolean) => void;
  /**
   * Text used for typeahead matching.
   */
  typeaheadValue?: string;
  /**
   * Close the menu when this item is activated.
   *
   * @default false
   */
  enableAutoClose?: boolean;
  /**
   * Disabled but still keyboard-focusable.
   *
   * @default false
   */
  isDisabled?: boolean;
}

type MenuMultiSelectItemProps = ComponentProps<
  MenuMultiSelectItemBaseProps,
  'div'
>;

type MenuMultiSelectItemComponent = Component<
  MenuMultiSelectItemBaseProps,
  'div'
>;

const MenuMultiSelectItem: MenuMultiSelectItemComponent = (props) => {
  const {
    isSelected,
    defaultSelected = false,
    onSelectedChange,
    typeaheadValue,
    enableAutoClose = false,
    isDisabled = false,
    className,
    children,
    ...rest
  } = props;

  const [trackedSelected, setTrackedSelected] = useState(defaultSelected);

  const handleCheckedChange = useCallback(
    (checked: boolean) => {
      setTrackedSelected(checked);
      onSelectedChange?.(checked);
    },
    [onSelectedChange]
  );

  const resolvedSelected = isSelected ?? trackedSelected;

  const selectionContext = useMemo(
    () => ({ isSelected: resolvedSelected }),
    [resolvedSelected]
  );

  const { interactionProps } = useMenuItemInteraction({ isDisabled });
  const itemContext = useMemo(() => ({ isDisabled }), [isDisabled]);

  return (
    <MenuItemContext.Provider value={itemContext}>
      <MenuSelectionContext.Provider value={selectionContext}>
        <BaseMenu.CheckboxItem
          {...rest}
          checked={isSelected}
          defaultChecked={defaultSelected}
          onCheckedChange={handleCheckedChange}
          label={typeaheadValue}
          closeOnClick={enableAutoClose}
          disabled={isDisabled}
          render={(baseProps) => (
            <div
              {...mergeProps(baseProps, interactionProps)}
              className={menuItemFocusStyles.root({
                className: menuMultiSelectItemStyles({ isDisabled, className }),
              })}
            />
          )}
        >
          {children}
        </BaseMenu.CheckboxItem>
      </MenuSelectionContext.Provider>
    </MenuItemContext.Provider>
  );
};

MenuMultiSelectItem.displayName = 'Menu.MultiSelectItem';

export {
  MenuMultiSelectItem,
  type MenuMultiSelectItemProps,
  type MenuMultiSelectItemBaseProps,
  menuMultiSelectItemClasses,
};
